/**
 * EntityStatusLogTab — shared "Status Log" tab body for Proposal, Lead, and
 * RFP detail panels. Mirrors the existing Project status log UX:
 *   - Rich-text status update + plain-text Action Items field
 *   - Two save buttons: [Add Status] and [Add Status + Log Time]
 *   - Card list of prior entries (newest first) with edit + delete
 *   - If an action item already spawned a To-Do, surface a link to it
 *
 * Designed so adding/editing entries refetches the parent's status_log
 * (via `onChange()`), and a fresh log row appears immediately without the
 * user needing to refresh the page.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Trash2, Clock, ListTodo } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import RichTextEditor from '@/components/RichTextEditor';
import RichTextDisplay from '@/components/RichTextDisplay';
import { QuickLogTimeSheet, htmlToText } from '@/components/QuickLogTimeSheet';

export function EntityStatusLogTab({
  entityType,    // 'proposal' | 'lead' | 'rfp'
  entityId,
  entityLabel,
  statusLog = [],
  onChange,      // parent refresh callback
}) {
  const [statusText, setStatusText] = useState('');
  const [actionItems, setActionItems] = useState('');
  const [saving, setSaving] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [editForm, setEditForm] = useState({ status_text: '', action_items: '' });

  // QuickLogTimeSheet state — pre-fills notes from the just-created status.
  const [logTimeOpen, setLogTimeOpen] = useState(false);
  const [logTimeNotes, setLogTimeNotes] = useState('');
  const [logTimeStatusId, setLogTimeStatusId] = useState(null);

  const reset = () => {
    setStatusText('');
    setActionItems('');
  };

  const addStatus = async (alsoLogTime = false) => {
    if (!statusText.trim()) return toast.error('Status text is required');
    try {
      setSaving(true);
      const r = await api.post(`/${entityType}/${entityId}/status-log`, {
        status_text: statusText,
        action_items: actionItems,
      });
      toast.success('Status added');
      // Capture the typed notes BEFORE we reset so the QuickLog sheet can
      // open with them already pre-filled.
      const carriedNotes = htmlToText(statusText);
      const newStatusId = r?.data?.id || null;
      reset();
      onChange?.();
      if (alsoLogTime) {
        setLogTimeNotes(carriedNotes);
        setLogTimeStatusId(newStatusId);
        setLogTimeOpen(true);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add status');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (entry) => {
    setEditEntry(entry);
    setEditForm({
      status_text: entry.status_text || '',
      action_items: entry.action_items || '',
    });
  };

  const saveEdit = async () => {
    if (!editForm.status_text.trim()) return toast.error('Status text is required');
    try {
      await api.put(`/${entityType}/${entityId}/status-log/${editEntry.id}`, editForm);
      toast.success('Status updated');
      setEditEntry(null);
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const remove = async (entry) => {
    if (!window.confirm('Delete this status entry? Any pending To-Do suggestion linked to it will also be removed.')) return;
    try {
      await api.delete(`/${entityType}/${entityId}/status-log/${entry.id}`);
      toast.success('Status deleted');
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="space-y-3" data-testid={`${entityType}-status-log-tab`}>
      {/* Composer */}
      <div className="space-y-2 border border-border/60 rounded-md bg-secondary/20 p-3">
        <RichTextEditor
          placeholder="Status update..."
          value={statusText}
          onChange={v => setStatusText(v)}
          collapsedHeight={80}
          data-testid={`${entityType}-status-text-input`}
        />
        <Input
          placeholder="Action items (optional)"
          value={actionItems}
          onChange={e => setActionItems(e.target.value)}
          className="bg-background border-border"
          data-testid={`${entityType}-status-action-input`}
        />
        <div className="flex gap-2 flex-wrap">
          <Button
            size="sm"
            onClick={() => addStatus(false)}
            disabled={saving}
            className="bg-primary text-primary-foreground"
            data-testid={`${entityType}-add-status-btn`}
          >
            <Plus className="h-3 w-3 mr-1" /> Add Status
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => addStatus(true)}
            disabled={saving}
            className="border-primary/40 text-primary hover:bg-primary/10"
            data-testid={`${entityType}-add-status-log-time-btn`}
          >
            <Clock className="h-3 w-3 mr-1" /> Add Status + Log Time
          </Button>
        </div>
      </div>

      {/* Inline edit form */}
      {editEntry && (
        <Card className="border-primary/30 border-dashed bg-primary/5 shadow-none" data-testid={`${entityType}-edit-status-form`}>
          <CardContent className="p-3 space-y-2">
            <p className="text-xs font-semibold text-primary">
              Edit Status Entry <span className="font-normal text-muted-foreground">· originally posted {new Date(editEntry.created_at).toLocaleString()}</span>
            </p>
            <RichTextEditor
              placeholder="Status text *"
              value={editForm.status_text}
              onChange={v => setEditForm(f => ({ ...f, status_text: v }))}
              collapsedHeight={80}
              data-testid={`${entityType}-edit-status-text`}
            />
            <Input
              placeholder="Action items"
              value={editForm.action_items}
              onChange={e => setEditForm(f => ({ ...f, action_items: e.target.value }))}
              className="bg-background border-border"
              data-testid={`${entityType}-edit-status-action`}
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={saveEdit} className="bg-primary text-primary-foreground" data-testid={`${entityType}-save-edit-status-btn`}>Save Changes</Button>
              <Button size="sm" variant="outline" onClick={() => setEditEntry(null)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* List */}
      <div className="space-y-2">
        {statusLog.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-6">No status updates yet. Add the first one above.</p>
        )}
        {statusLog.map(s => (
          <Card
            key={s.id}
            className="border-border bg-background shadow-none hover:border-primary/30 transition-colors"
            data-testid={`${entityType}-status-entry-${s.id}`}
          >
            <CardContent className="p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0 overflow-hidden break-words">
                  <RichTextDisplay value={s.status_text} collapsedHeight={40} />
                  {s.action_items && (
                    <div className="mt-1.5 flex items-start gap-1.5 flex-wrap">
                      <p className="text-xs text-muted-foreground">
                        <span className="text-amber-400 font-semibold">Action:</span> {s.action_items}
                      </p>
                      {s.linked_todo ? (
                        <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs gap-1" data-testid={`${entityType}-linked-todo-${s.id}`}>
                          <ListTodo className="h-3 w-3" /> To-Do: {s.linked_todo.status}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs text-muted-foreground gap-1">
                          <ListTodo className="h-3 w-3" /> Suggestion on To-Dos page
                        </Badge>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {s.created_by_name && <span>{s.created_by_name} · </span>}
                    {new Date(s.created_at).toLocaleString()}
                    {s.updated_at ? ` (edited ${new Date(s.updated_at).toLocaleString()})` : ''}
                  </p>
                </div>
                <div className="shrink-0 flex items-center gap-0.5">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 text-muted-foreground hover:text-primary"
                    onClick={() => openEdit(s)}
                    data-testid={`${entityType}-edit-status-${s.id}`}
                    title="Edit entry"
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 text-muted-foreground hover:text-destructive"
                    onClick={() => remove(s)}
                    data-testid={`${entityType}-delete-status-${s.id}`}
                    title="Delete entry"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <QuickLogTimeSheet
        open={logTimeOpen}
        onOpenChange={setLogTimeOpen}
        entityType={entityType}
        entityId={entityId}
        entityLabel={entityLabel}
        initialNotes={logTimeNotes}
        sourceStatusLogId={logTimeStatusId}
        sourceSurface="status_log_log_time"
        onLogged={onChange}
      />
    </div>
  );
}
